import React, { useEffect, useRef } from 'react';
import onClickWithoutFocus from '../../util/onClickWithoutFocus';
import { type MenuItemComponentType } from './types';
import classnames from 'classnames';

interface MenuItemProps<MenuItemObject> {
  item: MenuItemObject;
  index: number;
  onSelectItem: (index: number, event: KeyboardEvent | MouseEvent) => void;
  onMouseOverIndex: (index: number) => void;
  selected: boolean;
  observer: IntersectionObserver;
  MenuItemComponent: MenuItemComponentType<MenuItemObject>;
  className?: string;
}

const MenuItem = <MenuItemObject,>({
  item,
  index,
  onSelectItem,
  onMouseOverIndex,
  selected,
  observer,
  MenuItemComponent,
  className,
}: MenuItemProps<MenuItemObject>) => {
  const ListComponentRef = useRef<HTMLElement>(null);
  const onClick = (event: React.MouseEvent) => {
    event.preventDefault();
    onSelectItem(index, event.nativeEvent);
    return false;
  };

  useEffect(() => {
    const currentComponent = ListComponentRef.current;
    if (!currentComponent) return;
    if (!observer) return;
    if (!selected) return;
    observer.observe(currentComponent);

    return () => observer.unobserve(currentComponent);
  }, [observer, selected]);

  return (
    <a
      role="listitem"
      {...onClickWithoutFocus(onClick)}
      onMouseOver={() => onMouseOverIndex(index)}
      className={classnames(className)}
    >
      <MenuItemComponent
        item={item}
        selected={selected}
        ref={ListComponentRef}
      />
    </a>
  );
};

export default MenuItem;
